import { useState } from "react";
import { Rocket, KeyRound, Unplug, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

export function AyrshareSettings({ settings, onChanged }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const live = settings?.live;

  const save = async (value) => {
    setBusy(true);
    setMsg(null);
    try {
      await api.post("/social/settings", { ayrshare_api_key: value });
      setKey("");
      setMsg({ kind: "ok", text: value ? "Ayrshare connected — posts now go to your real accounts." : "Switched back to demo mode." });
      onChanged?.();
    } catch (e) {
      setMsg({ kind: "error", text: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border-2 border-[#2C3539]/10 p-6 md:p-8" data-testid="ayrshare-settings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display text-2xl md:text-3xl uppercase text-[#23282B] inline-flex items-center gap-3">
            Go Live
            <span data-testid="ayrshare-status" className={`text-[10px] font-sans font-bold tracking-[0.2em] px-2.5 py-1 ${live ? "bg-green-600 text-white" : "bg-[#23282B]/10 text-[#23282B]/60"}`}>
              {live ? "LIVE" : "DEMO MODE"}
            </span>
          </div>
          <p className="mt-1 text-sm text-[#23282B]/60 max-w-2xl">
            {live
              ? <>Connected to Ayrshare (key {settings.key_masked}). Posts from the composer below publish to the real accounts linked in your Ayrshare dashboard.</>
              : <>Connect an Ayrshare account to post to real Facebook, Instagram, LinkedIn and X accounts with one key. Until then, everything below is simulated.</>}
          </p>
        </div>
      </div>

      {!live && (
        <ol className="mt-4 text-sm text-[#23282B]/75 space-y-1.5 list-decimal list-inside">
          <li>Create a free account at <a href="https://www.ayrshare.com" target="_blank" rel="noreferrer" className="text-[#2F5F80] font-semibold underline underline-offset-2">ayrshare.com</a></li>
          <li>In the Ayrshare dashboard, link the company's Facebook / Instagram / LinkedIn accounts</li>
          <li>Copy the API Key from the Ayrshare "API Key" page and paste it below</li>
        </ol>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <input
          type="password"
          data-testid="ayrshare-key-input"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder={live ? "Paste a new key to replace the current one" : "Paste your Ayrshare API key"}
          className="flex-1 min-w-[240px] bg-white border-2 border-[#23282B]/15 focus:border-[#2F5F80] focus:outline-none px-3 py-3 text-sm text-[#23282B]"
        />
        <button
          onClick={() => key.trim() && save(key.trim())}
          disabled={busy || !key.trim()}
          data-testid="ayrshare-save-btn"
          className="inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] active:scale-95 text-white text-xs font-bold tracking-[0.15em] px-5 py-3.5 uppercase transition-all disabled:opacity-40"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : live ? <KeyRound size={14} /> : <Rocket size={14} />}
          {live ? "Update Key" : "Connect & Go Live"}
        </button>
        {live && (
          <button
            onClick={() => window.confirm("Switch back to demo mode? Posts will stop going to real accounts.") && save("")}
            disabled={busy}
            data-testid="ayrshare-disconnect-btn"
            className="inline-flex items-center gap-2 border-2 border-[#23282B]/20 hover:border-red-500 hover:text-red-600 text-[#23282B] text-xs font-bold tracking-[0.15em] px-4 py-3 uppercase transition-colors disabled:opacity-40"
          >
            <Unplug size={14} /> Back to Demo
          </button>
        )}
      </div>

      {msg && (
        <div data-testid={`ayrshare-msg-${msg.kind}`} className={`mt-4 px-4 py-3 text-sm border-l-4 ${msg.kind === "ok" ? "bg-green-50 border-green-600 text-green-800" : "bg-red-50 border-red-500 text-red-700"}`}>
          {msg.text}
        </div>
      )}
    </section>
  );
}
